import { SourceColorDialog } from './source-color-dialog'
import { adoptPageCommands } from '../router/page-commands'
import { pageRouteParams } from '../router/page-route-params'
import { unknownErrorToError } from '@w/utils'
import { ErrorDetails } from '@w/ui/components/error-details'
import { feEnv } from '@w/utils-fe/env'
import { useState } from 'react'
import { Button } from '@w/ui/components/button'
import { Avatar } from '@w/ui/components/avatar'
import { ToggleGroup, ToggleGroupItem } from '@w/ui/components/toggle-group'
import { Switch } from '@w/ui/components/switch'
import { IconButton } from '@w/ui/components/icon-button'
import { ChevronLeftIcon, RotateCwIcon } from '@w/ui/components/icon'
import { useTheme } from '@w/ui/components/theme-provider'
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@w/ui/components/select'
import { useTranslation } from '../i18n'
import {
  forcePwaReset,
  getPwaDisplayMode,
  requestPwaUpdate,
} from '../pwa-update'
import {
  useNavigate,
  useLoaderData,
  useRouterContext,
} from '../router/flashcards-fe.router-bindings'
import { getFlashcardsAppVersionDisplay } from '../server/flashcards-version-be'
import { ensureLanguagePage } from '../lexicon/lexicon-adoption'
import type { Language } from '../lexicon/lexicon-model'
import { LANGUAGES } from '../lexicon/lexicon-model'
import { ThemeToggle } from '../shell/app'

type UpdateState =
  | 'idle'
  | 'checking'
  | 'resetting'
  | 'current'
  | 'unavailable'
  | 'error'

export const SETTINGS_LANGUAGES = ['en', 'de'] as const

export function SettingsPage() {
  const { t } = useTranslation('settings')
  const { minimal, setMinimal, sourceColor, setSourceColor } = useTheme()
  const routerCtx = useRouterContext()
  const { auth } = routerCtx
  const { workspaceId } = useLoaderData({ from: '/settings' })
  const navigate = useNavigate()
  const [language, setLanguage] = useState<Language>('en')
  const [creatingLanguage, setCreatingLanguage] = useState(false)
  const [updateState, setUpdateState] = useState<UpdateState>('idle')
  const [updateError, setUpdateError] = useState<unknown>(null)
  const version = getFlashcardsAppVersionDisplay()
  const isWeb = feEnv().platform === 'web'
  const displayMode = isWeb ? getPwaDisplayMode() : null
  const user = auth.getUserOrNull()
  const signedIn = Boolean(user)
  const [nativeLanguage, setNativeLanguage] = useState(
    user?.settings.lexicon?.nativeLanguage ?? routerCtx.defaultNativeLanguage,
  )
  const [savingNativeLanguage, setSavingNativeLanguage] = useState(false)
  const [nativeLanguageError, setNativeLanguageError] = useState<unknown>(null)
  const [showViewHistory, setShowViewHistory] = useState(user?.settings.showViewHistory !== false)
  const [savingViewHistory, setSavingViewHistory] = useState(false)
  const [viewHistoryError, setViewHistoryError] = useState<unknown>(null)
  const nativeLanguageLabel = (value: string) => {
    if (value === 'en') return t('english')
    if (value === 'de') return t('german')
    if (value === 'ru') return t('russian')
    return value
  }

  async function saveNativeLanguage(value: string) {
    if (!user) return

    const saved = nativeLanguage
    setNativeLanguageError(null)
    setNativeLanguage(value)
    setSavingNativeLanguage(true)
    try {
      await auth.updateSettings({
        ...user.settings,
        lexicon: {
          ...user.settings.lexicon,
          nativeLanguage: value,
        },
      })
    } catch (error) {
      setNativeLanguage(saved)
      setNativeLanguageError(error)
    } finally {
      setSavingNativeLanguage(false)
    }
  }

  /** page-views.spec.md: turning view history off deletes the member's past views; the server then ignores their views. A failure leaves the switch where the server is. */
  async function saveViewHistory(value: boolean) {
    if (!user) return
    setViewHistoryError(null)
    setSavingViewHistory(true)
    try {
      // Turning it off is one idempotent server command (saves the setting and deletes past views); the switch moves only after it succeeded.
      if (value) await auth.updateSettings({ ...auth.getUser().settings, showViewHistory: true })
      else {
        await routerCtx.rpcClient.page.forgetMyViews.mutate({})
        // Not refreshUser: after this command it still returned the old settings (page-views.test.tsx opt-out test, 2026-10-19).
        await auth.updateSettings({ ...auth.getUser().settings, showViewHistory: false })
      }
      setShowViewHistory(value)
    } catch (error) {
      setViewHistoryError(error)
    } finally {
      setSavingViewHistory(false)
    }
  }

  async function openLanguagePage() {
    if (!(user && workspaceId)) return

    const own = routerCtx.spaces.snapshot(user.id).resources.find(value => value.space.kind === 'private' && value.space.ownerUserId === user.id)
    if (!own) throw new Error('Private space unavailable')
    setCreatingLanguage(true)
    try {
      const result = await ensureLanguagePage(
        { writes: routerCtx.writes,
          db: own.db,
          pageCommands: adoptPageCommands(routerCtx, own.space.id),
          tel: routerCtx.tel,
          afterCommit: routerCtx.afterCommit,
                afterLocalCommit: routerCtx.afterLocalCommit,
          spaceId: own.space.id,
          userId: user.id,
          deviceId: routerCtx.deviceId,
          now: () => new Date(),
        },
        language,
      )
      navigate({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(routerCtx, own.space.id, result.pageId),
      })
    } finally {
      setCreatingLanguage(false)
    }
  }

  async function checkForUpdate() {
    setUpdateState('checking')
    setUpdateError(null)

    try {
      const result = await requestPwaUpdate()
      setUpdateState(result)
    } catch (error) {
      setUpdateState('error')
      setUpdateError(error)
    }
  }

  async function resetPwa() {
    setUpdateState('resetting')
    setUpdateError(null)

    try {
      await forcePwaReset()
    } catch (error) {
      setUpdateState('error')
      setUpdateError(error)
    }
  }

  const status =
    updateState === 'current'
      ? t('updateCurrent')
      : updateState === 'unavailable'
        ? t('updateUnavailable')
        : updateState === 'error'
          ? t('updateFailed', { error: unknownErrorToError(updateError).message })
          : null

  return (
    <main className="bg-surface-1 text-on-surface min-h-screen px-5 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-lg flex-col gap-10">
        <section className="flex flex-col gap-10">
          <header className="bg-surface-1 sticky top-0 z-10 flex items-center gap-3 py-3">
            <div className="flex w-9 shrink-0 justify-start">
              <IconButton
                aria-label={signedIn ? t('backToApp') : t('backToLogin')}
                className="rounded-lg"
                data-testid="flashcards-settings-back"
                semanticRole="secondary"
                size="sm"
                title={signedIn ? t('backToApp') : t('backToLogin')}
                type="button"
                variant="text"
                onClick={() => {
                  navigate({ to: signedIn ? '/' : '/login' })
                }}
              >
                <ChevronLeftIcon />
                {signedIn ? t('backToApp') : t('backToLogin')}
              </IconButton>
            </div>
            <h1 className="min-w-0 flex-1 truncate text-center text-2xl font-semibold tracking-tight select-none">
              {t('title')}
            </h1>
            <div className="flex w-9 shrink-0 justify-end"><ThemeToggle /></div>
          </header>

          {user && (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="shrink-0" src={user.avatar ?? undefined} name={user.name ?? user.email} />
              <p className="min-w-0 text-on-surface-secondary text-sm break-all" data-testid="flashcards-settings-email">{user.email}</p>
            </div>
          )}

          <section className="flex w-full sm:w-fit max-w-full flex-col gap-5">
            <div className="flex flex-col gap-2 text-sm font-medium select-none">
              <span id="flashcards-palette-label">{t('palette')}</span>
              <ToggleGroup
                variant="outline"
                size="lg"
                className="w-full sm:w-fit"
                aria-labelledby="flashcards-palette-label"
                value={[minimal ? 'minimal' : 'chromatic']}
                onValueChange={(value) => {
                  if (value[0]) setMinimal(value[0] === 'minimal')
                }}
              >
                <ToggleGroupItem className="min-w-0 flex-1 rounded-s-full sm:flex-none" value="minimal">{t('minimal')}</ToggleGroupItem>
                <ToggleGroupItem className="min-w-0 flex-1 rounded-e-full sm:flex-none" value="chromatic">{t('chromatic')}</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="flex items-center justify-between gap-4 text-sm font-medium select-none">
              <span>{t('sourceHue')}</span>
              <SourceColorDialog value={sourceColor} onChange={setSourceColor} />
            </div>
          </section>
          {user && workspaceId ? (
            <div className="flex flex-col gap-10 medium:flex-row">
              <section className="flex flex-1 flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <h2 className="text-on-surface font-semibold select-none">
                    {t('nativeLanguage')}
                  </h2>
                  <p className="text-on-surface-secondary text-sm leading-6 select-none">
                    {t('nativeLanguageHelp')}
                  </p>
                </div>
                <label className="flex w-full sm:w-48 max-w-full flex-col gap-2 text-sm font-medium select-none">
                  {t('nativeLanguage')}
                  <Select
                    value={nativeLanguage}
                    disabled={savingNativeLanguage}
                    onValueChange={(value) => {
                      if (value) void saveNativeLanguage(value)
                    }}
                  >
                    <SelectTrigger data-testid="flashcards-native-language-select">
                      <SelectValue>
                        {nativeLanguageLabel(nativeLanguage)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectPopup align="start">
                      {LANGUAGES.map((value) => (
                        <SelectItem
                          key={value}
                          value={value}
                          data-testid={`flashcards-native-language-${value}`}
                        >
                          {nativeLanguageLabel(value)}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </label>
                {nativeLanguageError ? <ErrorDetails error={nativeLanguageError} compact /> : null}
                <div className="flex items-start justify-between gap-4 pt-5 select-none">
                  <span className="flex flex-col gap-2">
                    <span id="flashcards-view-history-label" className="text-on-surface font-semibold">{t('viewHistory')}</span>
                    <span className="text-on-surface-secondary text-sm leading-6">{t('viewHistoryHelp')}</span>
                  </span>
                  <Switch data-testid="flashcards-view-history" aria-labelledby="flashcards-view-history-label" checked={showViewHistory} disabled={savingViewHistory} onCheckedChange={checked => { void saveViewHistory(checked) }} />
                </div>
                {viewHistoryError ? <ErrorDetails error={viewHistoryError} compact /> : null}
              </section>

              <section className="border-separator flex flex-1 flex-col gap-5 border-t pt-10 medium:border-t-0 medium:pt-0">
                <div className="flex flex-col gap-2">
                  <h2 className="text-on-surface font-semibold select-none">
                    {t('languagePages')}
                  </h2>
                  <p className="text-on-surface-secondary text-sm leading-6 select-none">
                    {t('languagePagesHelp')}
                  </p>
                </div>
                <label className="flex w-full sm:w-48 max-w-full flex-col gap-2 text-sm font-medium select-none">
                  {t('language')}
                  <Select
                    value={language}
                    onValueChange={(value) => {
                      if (value === 'en' || value === 'de') setLanguage(value)
                    }}
                  >
                    <SelectTrigger data-testid="flashcards-language-select">
                      <SelectValue>
                        {language === 'en' ? t('english') : t('german')}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectPopup align="start">
                      {SETTINGS_LANGUAGES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value === 'en' ? t('english') : t('german')}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </label>
                <Button
                  className="w-full sm:w-auto sm:self-start"
                  data-testid="flashcards-open-language"
                  disabled={creatingLanguage}
                  type="button"
                  variant="filled"
                  onClick={() => {
                    void openLanguagePage()
                  }}
                >
                  {t('openLanguage')}
                </Button>
              </section>
            </div>
          ) : null}

          <section className="border-separator flex flex-col gap-8 border-t pt-10">
            <dl className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <dt className="text-on-surface-secondary text-sm">
                  {t('version')}
                </dt>
                <dd>
                  <code
                    className="text-on-surface font-mono text-sm font-medium break-all"
                    data-testid="flashcards-version"
                  >
                    {version}
                  </code>
                </dd>
              </div>
              {isWeb && <div className="flex flex-col gap-1">
                <dt className="text-on-surface-secondary text-sm">
                  {t('appMode')}
                </dt>
                <dd className="text-sm font-medium">
                  {displayMode === 'installed'
                    ? t('installedMode')
                    : t('browserMode')}
                </dd>
              </div>}
            </dl>

            <div className="flex flex-col gap-3">
              <Button
                className="w-full sm:w-auto sm:self-start"
                data-testid="flashcards-check-update"
                disabled={
                  updateState === 'checking' || updateState === 'resetting'
                }
                type="button"
                variant="filled"
                onClick={() => {
                  void checkForUpdate()
                }}
              >
                <RotateCwIcon />
                {updateState === 'checking' ? t('checking') : t('checkUpdate')}
              </Button>
              <p className="text-on-surface-secondary max-w-md text-sm leading-6">
                {status ?? t('updateHelp')}
              </p>
              {updateState === 'error' && <ErrorDetails error={updateError} compact />}
            </div>

            {isWeb && <div className="flex flex-col gap-3">
              <Button
                className="w-full sm:w-auto sm:self-start"
                data-testid="flashcards-force-reinstall"
                disabled={
                  updateState === 'checking' || updateState === 'resetting'
                }
                semanticRole="error"
                type="button"
                variant="outlined"
                onClick={() => {
                  void resetPwa()
                }}
              >
                <RotateCwIcon />
                {updateState === 'resetting'
                  ? t('reinstalling')
                  : t('forceReinstall')}
              </Button>
              <p className="text-on-surface-secondary max-w-md text-sm leading-6">
                {t('forceReinstallHelp')}
              </p>
            </div>}
          </section>
        </section>
      </div>
    </main>
  )
}
